import {Injectable} from "@angular/core";
import {BehaviorSubject, combineLatest, Observable, Subscriber} from "rxjs";
import {filter, map, switchMap, take} from "rxjs/operators";
import {notNull} from "@app/core/shared/domain/type.utils";
import {IsLoggedInUseCase} from "@app/core/user/application/isLoggedIn.use-case";
import {DisplayService} from "@app/presentation/services/display.service";
import {GetCartLineQuantityUseCase} from "@app/core/cart/application/get-cart-line-quantity.use-case";
import {Product} from "@app/core/product/domain/dtos/product";
import {AddProductUseCase} from "@app/core/cart/application/add-product.use-case";
import {RemoveProductUseCase} from "@app/core/cart/application/remove-product.use-case";
import {NoStockError} from "@app/core/stock/domain/errors/no-stock.error";
import {PopupNotifier} from "../errors/popup.notifier";
import {onIsLoadingChanges} from "@app/presentation/rxjs-utils/operators/on-is-loading-changes";
import {createStatusStore} from "@app/presentation/rxjs-utils/create-status-store";

@Injectable()
export class CartLineQuantitySetterViewModel {
    private productCodeSubject = new BehaviorSubject<string | null>(null)
    private productCode$: Observable<string> = this.productCodeSubject.asObservable().pipe(
        filter(notNull)
    );

    private promotionCodeSubject = new BehaviorSubject<string | null>(null);
    private promotionCode$: Observable<string | null> = this.promotionCodeSubject.asObservable();

    readonly cartLineQuantity$: Observable<number> = combineLatest([
        this.productCode$,
        this.promotionCode$,
    ]).pipe(
        switchMap(([productCode, promotionCode]) => {
            if (promotionCode) {
                return this.getCartLineQuantityUseCase.execute(productCode, promotionCode);
            }
            return this.getCartLineQuantityUseCase.execute(productCode, "");
        }),
    );

    private isLoggedIn$ = this.isLoggedInUseCase.execute();

    readonly quantityIsGreaterThanZero$ = this.cartLineQuantity$.pipe(
        map(quantity => quantity > 0)
    );

    readonly isMobile$ = this.displayService.isMobileDisplay$;

    //Display controllers

    readonly canDisplaySeePrice$: Observable<boolean> = this.isLoggedIn$.pipe(map(isLoggedIn => !isLoggedIn));

    readonly canDisplayQuantitySetter$: Observable<boolean> = combineLatest([
        this.isLoggedIn$,
        this.quantityIsGreaterThanZero$
    ]).pipe(
        map(([isLoggedIn, quantityIsGreaterThanZero]) => {
            return isLoggedIn && quantityIsGreaterThanZero;
        })
    )

    readonly canDisplayAddButton$: Observable<boolean> = combineLatest([
        this.isLoggedIn$,
        this.quantityIsGreaterThanZero$
    ]).pipe(
        map(([isLoggedIn, quantityIsGreaterThanZero]) => {
            return isLoggedIn && !quantityIsGreaterThanZero;
        }),
    );

    readonly canChangeQuantity$: Observable<boolean> = combineLatest(
        this.canDisplayQuantitySetter$,
        this.promotionCode$
    ).pipe(
        map(([canDisplayQuantitySetter, promotionCode]) =>
            canDisplayQuantitySetter && !promotionCode
        )
    )

    public notifier = new PopupNotifier();
    private notEnoughStockEmitter!: Subscriber<Product[]>;
    public notEnoughStock$: Observable<Product[]> = new Observable<Product[]>(emitter => this.notEnoughStockEmitter = emitter);

    private setQuantityStatus = createStatusStore();
    readonly isSetQuantityLoading$ = this.setQuantityStatus.isLoading$;

    constructor(
        private getCartLineQuantityUseCase: GetCartLineQuantityUseCase,
        private addProductUseCase: AddProductUseCase,
        private removeProductUseCase: RemoveProductUseCase,
        private isLoggedInUseCase: IsLoggedInUseCase,
        private displayService: DisplayService
    ) {
    }

    setProductCode(productCode: string) {
        this.productCodeSubject.next(productCode);
    }

    setPromotionCode(promotionCode: string) {
        this.promotionCodeSubject.next(promotionCode);
    }

    addToCart(product: Product) {
        this.setQuantity(product, 1);
    }

    setQuantity(product: Product, quantity: number) {
        // Remove product if quantity is 0 
        if (quantity == 0) {
            this.removeProductUseCase.execute(product).subscribe({
                // Send product code to force a quantity refresh
                next: () => this.productCodeSubject.next(product.code)
            });
            return;
        }

        // Add it to the cart if it's greater
        this.addProductUseCase.execute(product, quantity)
            .pipe(
                take(1),
                onIsLoadingChanges(this.setQuantityStatus.setIsLoading)
            )
            .subscribe({
                next: () => {
                    // Send product code to force a quantity refresh
                    this.productCodeSubject.next(product.code)
                },
                error: (error) => {
                    if (error instanceof NoStockError) {
                        // Notify which products don't have enough stock
                        this.notEnoughStockEmitter.next(error.noStockProducts);
                    } else {
                        // Notify other errors
                        this.notifier.notifyError(error);
                    }
                }
            });
    }
}


